"use client"
import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { getAuthHeader } from '../utils/auth';

const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

const Icons = {
    Chart: () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    User: () => <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
    Money: () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
    Zap: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>,
    Close: () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
};

export default function MarketingMetaDashboard() {
    const { clientId } = useOutletContext();
    const navigate = useNavigate();
    const [performanceData, setPerformanceData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [dateRange, setDateRange] = useState({ 
        start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0], 
        end: new Date().toISOString().split('T')[0] 
    });

    const [detailModal, setDetailModal] = useState({ isOpen: false, data: [], type: '', adName: '' });

    // 広告パフォーマンスデータの取得
    const fetchPerformance = async () => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams({ 
                client_id: clientId, 
                start: dateRange.start, 
                end: dateRange.end 
            });
            const res = await fetch(`${API_URL}/api/marketing/meta-performance?${params}`, { headers: getAuthHeader() });
            const data = await res.json();
            if (data.success) {
                setPerformanceData(data.data);
            }
        } catch (e) {
            console.error("Fetch Error:", e);
        } finally {
            setIsLoading(false);
        }
    };

    // 内訳データの取得（モーダル用）
    const fetchAdDetails = async (adId, type, adName) => {
        try {
            const params = new URLSearchParams({ 
                client_id: clientId, 
                ad_id: adId, 
                type: type, 
                start: dateRange.start, 
                end: dateRange.end 
            });
            const res = await fetch(`${API_URL}/api/marketing/meta-ad-details?${params}`, { headers: getAuthHeader() });
            const data = await res.json();
            if (data.success) {
                setDetailModal({ isOpen: true, data: data.data, type: type, adName: adName });
            }
        } catch (e) {
            console.error("Details Fetch Error:", e);
        }
    };

    useEffect(() => {
        if (clientId) fetchPerformance();
    }, [clientId, dateRange]);

    // ★ バグ修正箇所: total_sales ではなく total_revenue を参照するように修正
    const summary = useMemo(() => {
        return performanceData.reduce((acc, row) => ({
            spend: acc.spend + (Number(row.spend) || 0),
            leads: acc.leads + (Number(row.lead_count) || 0),
            deals: acc.deals + (Number(row.deal_count) || 0),
            sales: acc.sales + (Number(row.total_revenue) || 0), // 修正済み
            purchases: acc.purchases + (Number(row.purchase_count) || 0),
            clicks: acc.clicks + (Number(row.clicks) || 0)
        }), { spend: 0, leads: 0, deals: 0, sales: 0, purchases: 0, clicks: 0 });
    }, [performanceData]);

    const totalRoas = summary.spend > 0 ? ((summary.sales / summary.spend) * 100).toFixed(0) : 0;
    const totalCpa = summary.leads > 0 ? Math.floor(summary.spend / summary.leads) : 0;

    return (
        <div className="space-y-6 animate-in fade-in duration-500 pb-10">
            {/* ヘッダーセクション */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-black text-gray-900 uppercase tracking-tighter flex items-center gap-2">
                        <Icons.Chart /> Meta Ads ROI Dashboard
                    </h1>
                    <p className="text-sm text-gray-500 font-bold">広告費から最終成約までの投資対効果を可視化</p>
                </div>
                <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center gap-2 bg-white p-2 rounded-2xl shadow-sm border border-gray-100 sm:w-auto">
                    <input type="date" className="min-w-0 border-none text-sm font-bold focus:ring-0" value={dateRange.start} onChange={e => setDateRange({...dateRange, start: e.target.value})} />
                    <span className="text-gray-300 font-bold">~</span>
                    <input type="date" className="min-w-0 border-none text-sm font-bold focus:ring-0" value={dateRange.end} onChange={e => setDateRange({...dateRange, end: e.target.value})} />
                </div>
            </div>

            {/* サマリータイル（上部の大枠表示） */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
                {[
                    { label: 'Total Spend', value: `¥${summary.spend.toLocaleString()}`, color: 'text-gray-900' },
                    { label: 'Leads (CV)', value: `${summary.leads}件`, color: 'text-blue-600', sub: `CPA: ¥${totalCpa.toLocaleString()}` },
                    { label: 'Deals', value: `${summary.deals}件`, color: 'text-orange-600' },
                    { label: 'Sales (LTV)', value: `¥${summary.sales.toLocaleString()}`, color: 'text-green-600', icon: <Icons.Money /> },
                    { label: 'Total ROAS', value: `${totalRoas}%`, color: 'text-indigo-600', highlight: Number(totalRoas) >= 100 }
                ].map((card, i) => (
                    <div key={i} className="bg-white p-5 rounded-3xl shadow-sm border border-gray-100 relative group">
                        <p className="text-[10px] font-black uppercase text-gray-400 tracking-widest mb-1">{card.label}</p>
                        <p className={`text-xl font-black ${card.color} flex items-center gap-1`}>
                            {card.icon} {card.value}
                        </p>
                        {card.sub && <p className="text-[10px] text-gray-400 font-bold mt-1">{card.sub}</p>}
                        {card.highlight && <div className="absolute top-2 right-3 text-lg animate-bounce text-indigo-500">🔥</div>}
                    </div>
                ))}
            </div>

            {/* 指標を強化した一覧表 */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="divide-y divide-gray-50 md:hidden">
                    {isLoading ? (
                        <div className="p-8 sm:p-12 text-center animate-pulse font-bold text-gray-400 uppercase tracking-widest">Calculating Metrics...</div>
                    ) : performanceData.length === 0 ? (
                        <div className="p-8 sm:p-12 text-center text-gray-300 font-bold">指定期間内の広告データが見つかりません</div>
                    ) : performanceData.map((ad, idx) => {
                        const roas = ad.spend > 0 ? ((ad.total_revenue / ad.spend) * 100).toFixed(0) : 0;
                        const cpa = ad.lead_count > 0 ? Math.floor(ad.spend / ad.lead_count) : 0;
                        const cac = ad.purchase_count > 0 ? Math.floor(ad.spend / ad.purchase_count) : 0;
                        const ctr = ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(2) : '0.00';

                        return (
                            <div key={`${idx}-mobile`} className="p-4">
                                <div className="font-black text-gray-900 text-sm mb-0.5">{ad.ad_name || `ID: ${ad.ad_id}`}</div>
                                <div className="flex items-center gap-1.5 opacity-60">
                                    <span className="text-[8px] font-black text-white bg-gray-400 px-1 rounded">CAMPAIGN</span>
                                    <div className="text-[9px] text-gray-500 font-bold truncate">{ad.campaign_name || '---'}</div>
                                </div>
                                <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                                    <div className="rounded-2xl bg-blue-50/60 p-3 text-right">
                                        <p className="text-[9px] font-black uppercase text-blue-400">Spend / CTR</p>
                                        <p className="font-mono font-black text-gray-800">¥{Number(ad.spend).toLocaleString()}</p>
                                        <p className="text-[9px] text-blue-400 font-bold">{ad.clicks || 0} CLKS / {ctr}%</p>
                                    </div>
                                    <button onClick={() => fetchAdDetails(ad.ad_id, 'leads', ad.ad_name)} className="rounded-2xl bg-white p-3 text-center border border-blue-100">
                                        <p className="text-[9px] font-black uppercase text-gray-400">Leads</p>
                                        <p className="text-blue-600 font-black text-base underline decoration-2">{ad.lead_count}</p>
                                        <p className="text-[9px] text-gray-400 font-black">CPA: ¥{cpa.toLocaleString()}</p>
                                    </button>
                                    <button onClick={() => fetchAdDetails(ad.ad_id, 'deals', ad.ad_name)} className="rounded-2xl bg-white p-3 text-center border border-orange-100">
                                        <p className="text-[9px] font-black uppercase text-gray-400">Deals</p>
                                        <p className="text-orange-500 font-black text-base underline decoration-2">{ad.deal_count}</p>
                                    </button>
                                    <button onClick={() => fetchAdDetails(ad.ad_id, 'purchases', ad.ad_name)} className="rounded-2xl bg-green-50 p-3 text-center border border-green-100">
                                        <p className="text-[9px] font-black uppercase text-green-700">Purchases</p>
                                        <p className="font-black text-green-700">{ad.purchase_count}件</p>
                                        <p className="text-[9px] text-green-700 font-black">CAC: ¥{cac.toLocaleString()}</p>
                                    </button>
                                </div>
                                <div className="mt-3 rounded-2xl bg-green-50/60 p-3 text-right">
                                    <p className="text-[9px] font-black uppercase text-green-700">Sales / ROAS</p>
                                    <p className="font-mono font-black text-green-600">¥{Number(ad.total_revenue).toLocaleString()}</p>
                                    <p className={`text-[11px] font-black ${Number(roas) >= 100 ? 'text-indigo-600' : 'text-gray-400'}`}>ROAS: {roas}%</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="hidden overflow-x-auto md:block">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-[9px] font-black text-gray-400 uppercase tracking-widest border-b">
                            <tr>
                                <th className="p-5 min-w-[220px]">Meta 広告階層</th>
                                <th className="p-5 text-right bg-blue-50/30">消化金額 / CTR</th>
                                <th className="p-5 text-center">リード獲得 (CPA)</th>
                                <th className="p-5 text-center">案件化</th>
                                <th className="p-5 text-center bg-green-50/30 font-black text-green-700">実成約 (CAC)</th>
                                <th className="p-5 text-right bg-green-50/30">実売上 / ROAS</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {isLoading ? (
                                <tr><td colSpan="6" className="p-20 text-center animate-pulse font-bold text-gray-400 uppercase tracking-widest">Calculating Metrics...</td></tr>
                            ) : performanceData.length === 0 ? (
                                <tr><td colSpan="6" className="p-20 text-center text-gray-300 font-bold">指定期間内の広告データが見つかりません</td></tr>
                            ) : performanceData.map((ad, idx) => {
                                // 運用指標の計算
                                const roas = ad.spend > 0 ? ((ad.total_revenue / ad.spend) * 100).toFixed(0) : 0;
                                const cpa = ad.lead_count > 0 ? Math.floor(ad.spend / ad.lead_count) : 0;
                                const cac = ad.purchase_count > 0 ? Math.floor(ad.spend / ad.purchase_count) : 0;
                                const ctr = ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(2) : '0.00';

                                return (
                                    <tr key={idx} className="group hover:bg-gray-50/80 transition-colors">
                                        <td className="p-5">
                                            <div className="font-black text-gray-900 text-sm mb-0.5">{ad.ad_name || `ID: ${ad.ad_id}`}</div>
                                            <div className="flex items-center gap-1.5 opacity-60">
                                                <span className="text-[8px] font-black text-white bg-gray-400 px-1 rounded">CAMPAIGN</span>
                                                <div className="text-[9px] text-gray-500 font-bold truncate max-w-[150px]">{ad.campaign_name || '---'}</div>
                                            </div>
                                        </td>
                                        
                                        {/* 金額とクリック効率 */}
                                        <td className="p-5 text-right bg-blue-50/10">
                                            <div className="font-mono font-black text-gray-800 text-sm">¥{Number(ad.spend).toLocaleString()}</div>
                                            <div className="text-[9px] text-blue-400 font-bold mt-0.5 uppercase tracking-tighter">
                                                {ad.clicks || 0} CLKS / {ctr}% CTR
                                            </div>
                                        </td>
                                        
                                        {/* リード獲得とCPA */}
                                        <td className="p-5 text-center">
                                            <button 
                                                onClick={() => fetchAdDetails(ad.ad_id, 'leads', ad.ad_name)}
                                                className="inline-flex flex-col items-center hover:scale-110 transition-transform cursor-pointer"
                                            >
                                                <span className="text-blue-600 font-black text-base underline decoration-2">{ad.lead_count}</span>
                                                <span className="text-[9px] text-gray-400 font-black">CPA: ¥{cpa.toLocaleString()}</span>
                                            </button>
                                        </td>

                                        {/* 案件化数 */}
                                        <td className="p-5 text-center">
                                            <button 
                                                onClick={() => fetchAdDetails(ad.ad_id, 'deals', ad.ad_name)}
                                                className="inline-flex flex-col items-center hover:scale-110 transition-transform cursor-pointer"
                                            >
                                                <span className="text-orange-500 font-black text-base underline decoration-2">{ad.deal_count}</span>
                                                <span className="text-[8px] text-gray-300 font-bold uppercase tracking-widest">Deals</span>
                                            </button>
                                        </td>

                                        {/* 実成約数と獲得コスト(CAC) */}
                                        <td className="p-5 text-center bg-green-50/10 text-xs">
                                            <button 
                                                onClick={() => fetchAdDetails(ad.ad_id, 'purchases', ad.ad_name)}
                                                className="inline-flex flex-col items-center hover:scale-110 transition-transform cursor-pointer"
                                            >
                                                <span className="px-3 py-1 bg-green-600 text-white rounded-lg font-black text-[10px] shadow-sm mb-1">
                                                    {ad.purchase_count}件
                                                </span>
                                                <span className="text-[9px] text-green-700 font-black">CAC: ¥{cac.toLocaleString()}</span>
                                            </button>
                                        </td>

                                        {/* 売上と投資対効果 */}
                                        <td className="p-5 text-right bg-green-50/10">
                                            <div className="font-mono font-black text-green-600 text-sm italic underline decoration-green-200 decoration-wavy">¥{Number(ad.total_revenue).toLocaleString()}</div>
                                            <div className={`text-[11px] font-black mt-0.5 ${Number(roas) >= 100 ? 'text-indigo-600' : 'text-gray-300'}`}>
                                                ROAS: {roas}%
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* 詳細表示モーダル（内訳） */}
            {detailModal.isOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in duration-200">
                        <div className="p-4 sm:p-6 border-b flex justify-between items-start gap-4 bg-gray-50">
                            <div className="min-w-0">
                                <h3 className="text-base sm:text-lg font-black text-gray-900 uppercase tracking-tighter">
                                    {detailModal.type === 'leads' ? '📥 獲得リード内訳' : detailModal.type === 'deals' ? '📦 案件化リスト' : '✅ 成約顧客リスト'}
                                </h3>
                                <p className="break-words text-[10px] text-gray-500 font-bold uppercase tracking-widest">{detailModal.adName || 'Ad Detail'}</p>
                            </div>
                            <button onClick={() => setDetailModal({...detailModal, isOpen: false})} className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-400">
                                <Icons.Close />
                            </button>
                        </div>
                        <div className="max-h-[60vh] overflow-y-auto p-3 sm:p-4">
                            <div className="overflow-x-auto">
                            <table className="w-full min-w-[520px] text-sm">
                                <thead className="text-[10px] text-gray-400 uppercase font-black border-b">
                                    <tr>
                                        <th className="p-3 text-left">会社名 / 氏名</th>
                                        <th className="p-3 text-left">日付</th>
                                        <th className="p-3 text-right">操作</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {detailModal.data.length === 0 ? (
                                        <tr><td colSpan="3" className="p-8 sm:p-16 text-center text-gray-300 font-bold italic tracking-widest uppercase">No Data Found</td></tr>
                                    ) : detailModal.data.map((item, i) => (
                                        <tr key={i} className="group hover:bg-indigo-50/30 transition-colors">
                                            <td className="p-3">
                                                <div className="font-bold text-gray-800">{item.company_name}</div>
                                                <div className="text-[10px] text-gray-400 font-medium">{item.name}</div>
                                            </td>
                                            <td className="p-3 text-[10px] text-gray-400 font-mono">
                                                {new Date(item.created_at).toLocaleDateString('ja-JP')}
                                            </td>
                                            <td className="p-3 text-right">
                                                <button 
                                                    onClick={() => {
                                                        let path = '/marketing/leads';
                                                        if (detailModal.type === 'deals') path = '/deals';
                                                        if (detailModal.type === 'purchases') path = '/customer-ledger';

                                                        navigate(`${path}?searchTerm=${encodeURIComponent(item.company_name)}`);
                                                    }}
                                                    className="text-[9px] font-black bg-white border border-gray-200 text-gray-500 px-3 py-1 rounded-full hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-all shadow-sm active:scale-95"
                                                >
                                                    表示して操作
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
